import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { invocationSessionCondition } from "../invocationSearchConditions";

/** The query key prefix of a session's active invocations, for invalidating after a cancel. */
export function activeSessionInvocationsQueryKey(sessionId: string) {
  return ["activeSessionInvocations", sessionId];
}

/**
 * The invocations whose input names the session that are still queued or running.
 *
 * Polls every 5 seconds. Disabled until a session ID and an organization are known.
 */
export function useActiveSessionInvocations(sessionId: string | undefined) {
  const { currentOrganization } = useAuth();
  const { actionService } = useDomainServices();

  return useQuery({
    queryKey: [
      ...activeSessionInvocationsQueryKey(sessionId ?? ""),
      currentOrganization?.org_id,
    ],
    queryFn: async ({ signal }) => {
      if (currentOrganization === null) {
        throw new Error("Requires login");
      }
      if (sessionId === undefined) {
        throw new Error("Requires sessionId");
      }

      return actionService.queryActiveInvocations(
        currentOrganization.org_id,
        invocationSessionCondition(sessionId),
        signal,
      );
    },
    enabled: currentOrganization !== null && sessionId !== undefined,
    refetchInterval: 5_000,
  });
}
